// ============================================================================
// L'EAN-13 ET LE GENCODE INTERNE — la logique PURE, sans base ni écran.
//
// UTILITÉ : un article sans code-barres fournisseur (dépannage, marque propre, article créé au
// back-office) doit pouvoir en recevoir un. Ce code doit être un EAN-13 VALIDE — sinon aucune douchette
// ne le lira — et il ne doit JAMAIS pouvoir désigner un produit du commerce.
//
// ⚠️ LE FORMAT EST VERROUILLÉ PAR LA SPEC (§24.9quater e), et il sature exactement les 13 positions :
//
//     2 │ RRRRR │ NNNNNN │ C
//     │    │        │       └─ clé de contrôle mod-10
//     │    │        └───────── séquence du magasin, 6 chiffres → 1 000 000 de codes
//     │    └────────────────── radical du magasin, 5 chiffres, attribué au provisioning et FIGÉ
//     └─────────────────────── plage GS1 « usage interne » (2x), réservée : jamais un EAN commerce
//
// ⚠️ POURQUOI 10⁶ ET PAS PLUS : ni 10⁷ ni 10¹⁰ ne tiennent. Élargir la séquence obligerait à rogner le
// radical (deux magasins finiraient par se marcher dessus) ou à déborder les 13 chiffres (ce ne serait
// plus un EAN). La capacité par magasin est une CONSÉQUENCE du format, pas un réglage.
//
// ⚠️ ANTI-COLLISION SUR TROIS AXES, et il faut les trois : le préfixe `2` sépare du monde réel, le
// radical sépare les magasins entre eux, la séquence sépare les codes d'un même magasin. En retirer un
// seul rouvre une famille entière de collisions.
//
// ⚠️ ZÉRO DÉPENDANCE, comme tout ce paquet : la caisse (hors ligne, sous-plages pré-allouées) et le
// back-office doivent appliquer EXACTEMENT la même règle. Une règle que les deux bouts appliquent ne
// peut pas vivre dans l'un des deux.
// ============================================================================

/** Longueur d'un EAN-13, clé comprise. */
export const EAN13_LENGTH = 13;

/** Longueur du radical de magasin, en chiffres. */
export const EAN_RADICAL_LENGTH = 5;

/** Longueur de la séquence, en chiffres. */
export const EAN_SEQUENCE_LENGTH = 6;

/**
 * Capacité d'un magasin : 10⁶ codes internes.
 * ⚠️ Ce n'est pas un plafond qu'on peut relever — voir l'en-tête. Le dépassement doit donc être une
 * ERREUR NOMMÉE et pas un repli silencieux : réutiliser un code déjà émis ferait scanner un article
 * pour un autre, en caisse, sur un ticket fiscal.
 */
export const EAN_INTERNAL_CAPACITY = 10 ** EAN_SEQUENCE_LENGTH;

/** Le chiffre de tête de la plage « usage interne » réservée par GS1. */
export const EAN_INTERNAL_PREFIX = '2';

/**
 * La CLÉ DE CONTRÔLE mod-10 d'un EAN-13, calculée sur ses 12 premiers chiffres.
 *
 * ⚠️ C'EST ELLE QUI REND LE CODE SCANNABLE. Une douchette rejette un code dont la clé est fausse : sans
 * elle, l'article existerait en base et resterait invendable — un défaut qu'on ne découvre qu'en caisse,
 * client devant. La règle : somme pondérée 1,3,1,3… de gauche à droite, puis complément à la dizaine.
 */
export function ean13CheckDigit(douze: string): string {
  if (!/^\d{12}$/.test(douze)) {
    throw new Error('ean13CheckDigit: 12 chiffres attendus');
  }
  let sum = 0;
  for (let i = 0; i < 12; i += 1) {
    // Position paire (0-indexée) = poids 1, impaire = poids 3.
    sum += Number(douze[i]) * (i % 2 === 0 ? 1 : 3);
  }
  return String((10 - (sum % 10)) % 10);
}

/** Un EAN-13 est-il bien formé ET correctement clé ? */
export function isValidEan13(code: string): boolean {
  if (!/^\d{13}$/.test(code)) return false;
  return ean13CheckDigit(code.slice(0, 12)) === code[12];
}

/**
 * Le gencode interne d'un magasin pour un numéro de séquence donné.
 *
 * ⚠️ FONCTION PURE ET TOTALE : à radical et séquence égaux, elle rend TOUJOURS le même code. C'est ce
 * qui permet de rejouer une écriture sans inventer un second code pour le même article — et c'est aussi
 * ce qui permet de VÉRIFIER, après coup, qu'un code émis était bien le bon.
 * ⚠️ Le dépassement de capacité LÈVE, il ne boucle pas : à 10⁶, un modulo silencieux réattribuerait le
 * code du tout premier article. Deux articles, un seul code-barres, en caisse.
 */
export function composeInternalEan(radical: string, sequence: number): string {
  if (!/^\d{5}$/.test(radical)) {
    throw new Error(`composeInternalEan: radical « ${radical} » invalide (5 chiffres attendus)`);
  }
  if (!Number.isInteger(sequence) || sequence < 0) {
    throw new Error(`composeInternalEan: séquence « ${sequence} » invalide (entier ≥ 0 attendu)`);
  }
  if (sequence >= EAN_INTERNAL_CAPACITY) {
    throw new Error(
      `composeInternalEan: séquence ${sequence} au-delà de la capacité du format (${EAN_INTERNAL_CAPACITY} codes par magasin)`,
    );
  }
  const douze = EAN_INTERNAL_PREFIX + radical + String(sequence).padStart(EAN_SEQUENCE_LENGTH, '0');
  return douze + ean13CheckDigit(douze);
}

/**
 * Ce qu'un gencode interne dit de lui-même — ou `null` s'il n'en est pas un.
 *
 * ⚠️ SERT À DEUX CHOSES, et la seconde est la plus importante : reconnaître un code « maison » à
 * l'écran, et surtout RETROUVER LA SÉQUENCE ATTEINTE dans une base dont personne n'a tenu le compteur
 * (reprise legacy, base restaurée). Sans ce décodage, on repartirait de zéro et on réémettrait des
 * codes déjà en rayon.
 * ⚠️ La clé est VÉRIFIÉE : un code à clé fausse n'est pas « un interne un peu abîmé », c'est un code
 * qu'aucune douchette ne rendra — le traiter comme nôtre nous ferait compter une séquence imaginaire.
 */
export function parseInternalEan(code: string): { radical: string; sequence: number } | null {
  if (!isValidEan13(code)) return null;
  if (code[0] !== EAN_INTERNAL_PREFIX) return null;
  const radical = code.slice(1, 1 + EAN_RADICAL_LENGTH);
  const sequence = Number(code.slice(1 + EAN_RADICAL_LENGTH, 1 + EAN_RADICAL_LENGTH + EAN_SEQUENCE_LENGTH));
  return { radical, sequence };
}

/**
 * Le radical, tel qu'il se LIT sur une étiquette — avec son « 2 » de tête.
 * ⚠️ Décision utilisateur (2026-08-14, backoffice-features.md §72) : le radical est STOCKÉ nu
 * (`00004`) mais ne s'affiche jamais ainsi. C'est `200004` qui ouvre chaque code-barres du magasin, et
 * c'est ce motif-là qu'on cherche des yeux en rayon ; le radical nu ne ressemble à rien de lisible.
 */
export function radicalComplet(radical: string): string {
  return EAN_INTERNAL_PREFIX + radical;
}

/** Un radical est-il utilisable ? ⚠️ Cinq chiffres EXACTEMENT — « 4 » et « 00004 » ne sont pas le même magasin. */
export function isValidRadical(radical: string): boolean {
  return /^\d{5}$/.test(radical);
}

// ── LE PLAFOND DES RADICAUX ATTRIBUABLES ──────────────────────────────────────────────────────────
//
// ⚠️ HISTORIQUE (2026-08-30, même jour) : une 2ᵉ famille de codes internes (« 29 » = cartes de
// fidélité) a été conçue puis RETIRÉE le jour même — remplacée par les cartes au format n° client
// `CLI-S…R…-seq` (code128, comme les titres — aucune seconde grammaire EAN, aucune ambiguïté de
// découpage avec les articles). La migration Console 0032, déjà partie (scellée), plafonne la
// séquence des radicaux à 89999 : le plafond RESTE — c'est une marge inoffensive (89 999 magasins),
// et retoucher une migration partie est interdit (Forme L). Cette constante l'épingle.
export const RADICAL_MAX_ATTRIBUABLE = 89999;
